import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { createSequenceTemplate, getSequenceTemplates, getSequenceTemplateById, deleteSequenceTemplate } from '../../utils/Product';
import { toast } from 'react-toastify';
import { FaInfoCircle, FaTrash, FaEdit } from 'react-icons/fa';
import EditSequenceTemplateModal from './EditSequenceTemplateModal';
import ConfirmDeleteTemplateModal from './ConfirmDeleteTemplateModal';
import './SequenceTemplateModal.css';

const SequenceTemplateModal = ({ 
  show, 
  onHide, 
  currentSectionOrder = [], 
  productId,
  onApplyTemplate 
}) => {
  const [activeTab, setActiveTab] = useState('save'); // 'save' or 'apply'
  const [templateTitle, setTemplateTitle] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const iconRefs = useRef({});

  // Load templates when modal opens
  useEffect(() => {
    if (show && activeTab === 'apply') {
      loadTemplates();
    }
  }, [show, activeTab]);

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSequenceTemplates();
      // Handle different response structures
      const templatesData = response?.data || response || [];
      // Filter out inactive sequences - only show active ones
      const activeTemplates = Array.isArray(templatesData) 
        ? templatesData.filter(template => template.status !== 'inactive')
        : [];
      setTemplates(activeTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load sequence templates. Please try again.');
      toast.error('Failed to load sequence templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateTitle.trim()) {
      setError('Template title is required');
      return;
    }

    if (!currentSectionOrder || currentSectionOrder.length === 0) {
      setError('No section order available. Please ensure sections are arranged.');
      return;
    }

    if (!productId) {
      setError('Product must be saved before creating a template. Please save the product first.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const templateData = {
        content_type: 'Product',
        section_order: currentSectionOrder,
        title: templateTitle.trim(),
        status: 'active'
      };

      await createSequenceTemplate(templateData);
      toast.success('Sequence template saved successfully!');
      setTemplateTitle('');
      setActiveTab('apply'); // Switch to apply tab after saving
      loadTemplates(); // Refresh templates list
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save template';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    if (!templateId) {
      toast.error('Invalid template selected');
      return;
    }

    setApplying(templateId);
    setError('');

    try {
      const response = await getSequenceTemplateById(templateId);
      const template = response?.data || response;
      
      if (!template || !template.section_order) {
        throw new Error('Invalid template data');
      }

      // Call the parent callback to apply the template
      if (onApplyTemplate) {
        onApplyTemplate(template.section_order);
        toast.success(`Template "${template.title || 'Untitled'}" applied successfully!`);
        onHide();
      }
    } catch (error) {
      console.error('Error applying template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to apply template';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setApplying(null);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
  };

  const handleTemplateUpdated = () => {
    loadTemplates();
  };

  const handleTemplateDeleted = () => {
    loadTemplates();
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (templateId, templateTitle) => {
    if (!templateId) {
      toast.error('Invalid template selected');
      return;
    }

    setTemplateToDelete({ id: templateId, title: templateTitle });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) {
      return;
    }

    setShowDeleteConfirm(false);
    setDeleting(templateToDelete.id);
    setError('');

    try {
      await deleteSequenceTemplate(templateToDelete.id);
      
      toast.success(`Template "${templateToDelete.title || 'Untitled'}" deleted successfully!`);
      // Refresh the templates list
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete template';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeleting(null);
      setTemplateToDelete(null);
    }
  };

  const handleClose = () => {
    setTemplateTitle('');
    setError('');
    setActiveTab('save');
    setTemplates([]);
    onHide();
  };

  if (!show) return null;

  return (
    <div className="sequence-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="sequence-modal">
        <div className="sequence-modal-header">
          <h3 className="sequence-modal-title">Sequence Templates</h3>
          <button className="sequence-close-button" onClick={handleClose}>
            &times;
          </button>
        </div>
        
        {/* Tabs */}
        <div className="sequence-tabs">
          <button
            className={`sequence-tab ${activeTab === 'save' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('save');
              setError('');
            }}
          >
            Save Current Sequence
          </button>
          <button
            className={`sequence-tab ${activeTab === 'apply' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('apply');
              setError('');
              loadTemplates();
            }}
          >
            Apply Template
          </button>
        </div>

        <div className="sequence-modal-body">
          {error && (
            <div className="sequence-error">
              {error}
            </div>
          )}

          {/* Save Tab */}
          {activeTab === 'save' && (
            <div className="sequence-tab-content">
              <p className="sequence-description">
                Save the current section order as a template that can be applied to other products.
              </p>
              
              {!productId && (
                <div className="sequence-warning">
                  <strong>Note:</strong> The product must be saved before creating a template. 
                  Please save the product first.
                </div>
              )}
              
              <form onSubmit={handleSaveTemplate}>
                <div className="sequence-form-group">
                  <label className="sequence-label">
                    Template Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="sequence-input"
                    value={templateTitle}
                    onChange={(e) => {
                      setTemplateTitle(e.target.value);
                      setError('');
                    }}
                    placeholder="e.g., Collections, Standard Product, etc."
                    required
                    autoFocus
                    disabled={saving || !productId}
                  />
                </div>
              </form>
            </div>
          )}

          {/* Apply Tab */}
          {activeTab === 'apply' && (
            <div className="sequence-tab-content">
              <p className="sequence-description">
                Select a template to apply its section order to the current product.
              </p>

              {loading ? (
                <div className="sequence-loading">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading templates...</span>
                  </Spinner>
                </div>
              ) : templates.length === 0 ? (
                <div className="sequence-info">
                  No sequence templates found. Create one by saving your current sequence.
                </div>
              ) : (
                <div className="sequence-templates-list">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="sequence-template-item"
                    >
                      <div className="sequence-template-info">
                        <div className="sequence-template-header">
                          <span className="sequence-template-title">{template.title || 'Untitled Template'}</span>
                          {template.section_order && Array.isArray(template.section_order) && template.section_order.length > 0 && (
                            <div 
                              className="sequence-info-icon"
                              ref={(el) => iconRefs.current[template.id] = el}
                              onMouseEnter={() => {
                                const iconEl = iconRefs.current[template.id];
                                if (iconEl) {
                                  const rect = iconEl.getBoundingClientRect();
                                  setTooltipPosition({
                                    top: rect.top + rect.height / 2,
                                    left: rect.left - 10
                                  });
                                }
                                setHoveredTooltip(template.id);
                              }}
                              onMouseLeave={(e) => {
                                // Check if moving to the tooltip
                                const relatedTarget = e.relatedTarget;
                                if (relatedTarget && relatedTarget.closest('.sequence-tooltip-portal')) {
                                  return; // Don't hide if moving to tooltip
                                }
                                setTimeout(() => setHoveredTooltip(null), 100);
                              }}
                            >
                              <FaInfoCircle />
                            </div>
                          )}
                        </div>
                        <span className="sequence-template-meta">
                          {template.section_order && Array.isArray(template.section_order) 
                            ? `${template.section_order.length} sections`
                            : 'No sections'}
                        </span>
                      </div>
                      <div className="sequence-template-actions">
                        {/* Edit button */}
                        <button
                          className="sequence-btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTemplate(template);
                          }}
                          disabled={applying === template.id}
                          title="Edit template"
                        >
                          <FaEdit />
                        </button>
                        {/* Delete button - commented out for now
                        {deleting === template.id ? (
                          <Spinner animation="border" size="sm" className="sequence-spinner-danger" />
                        ) : (
                          <button
                            className="sequence-btn-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template.id, template.title);
                            }}
                            disabled={applying === template.id}
                            title="Delete template"
                          >
                            <FaTrash />
                          </button>
                        )}
                        */}
                        {applying === template.id ? (
                          <Spinner animation="border" size="sm" className="sequence-spinner" />
                        ) : (
                          <button
                            className="sequence-btn-apply"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApplyTemplate(template.id);
                            }}
                          >
                            Apply
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="sequence-modal-footer">
        
          {activeTab === 'save' && (
            <button 
              className="sequence-btn-primary"
              onClick={handleSaveTemplate}
              disabled={!templateTitle.trim() || saving || !productId}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Template'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Tooltip Portal - renders outside modal to avoid clipping */}
      {hoveredTooltip && templates.find(t => t.id === hoveredTooltip) && ReactDOM.createPortal(
        <div 
          className="sequence-tooltip-portal"
          style={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: 'translate(-100%, -50%)',
            zIndex: 99999
          }}
          onMouseEnter={() => {
            // Keep tooltip visible when hovering over it
            setHoveredTooltip(hoveredTooltip);
          }}
          onMouseLeave={(e) => {
            // Check if moving back to the icon
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && relatedTarget.closest('.sequence-info-icon')) {
              return; // Don't hide if moving back to icon
            }
            setHoveredTooltip(null);
          }}
        >
          <div className="sequence-tooltip-title">Section Order:</div>
          <div className="sequence-tooltip-content">
            {templates.find(t => t.id === hoveredTooltip)?.section_order?.map((section, index) => (
              <div key={index} className="sequence-tooltip-item">
                <span className="sequence-tooltip-number">{index + 1}.</span>
                <span className="sequence-tooltip-text">{section}</span>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <EditSequenceTemplateModal
          show={!!editingTemplate}
          onHide={() => setEditingTemplate(null)}
          templateId={editingTemplate.id}
          templateTitle={editingTemplate.title}
          currentSectionOrder={currentSectionOrder}
          onApplyTemplate={onApplyTemplate}
          onTemplateUpdated={handleTemplateUpdated}
          onTemplateDeleted={handleTemplateDeleted}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteTemplateModal
        show={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        templateTitle={templateToDelete?.title}
      />
    </div>
  );
};

export default SequenceTemplateModal;
